import type { ActivityLogDto } from '../types';

/**
 * Collapsing runs of related rows for display (E9 PART E).
 *
 * ## The measured problem
 *
 * Picklist 1745 produced **49 activity rows in one day** against a page size of 25. Rendered flat, a single
 * afternoon of picking buries every other event on the record and makes the sticky day header useless.
 *
 * ## Two grouping keys, because one cannot do the job
 *
 * **E-1, exact — by `correlationId`.** One HTTP request mints one correlation id, so this collapses the rows
 * a single *request* produced. The founding case is a final pick emitting `PickRecorded` **and**
 * `PicklistFulfilled` in the same transaction, which reads as two separate user actions a second apart.
 * Deterministic; no window, no heuristic.
 *
 * **E-2, heuristic — by actor + action, inside a time window.** 39 sequential picks are 39 separate HTTP
 * requests and therefore 39 distinct correlation ids, so E-1 cannot touch them. This one can.
 *
 * ## What grouping is NOT allowed to do
 *
 * The owner's rule is locked: **one submission is one row.** A group is a display container — nothing is
 * merged, summed, deleted, or synthesised. Every row inside keeps its own actor, time and evidence, and the
 * count of rows across all groups always equals the count the API returned. That property is asserted.
 */

/** Rows closer together than this can join one burst. Tuned by eye against a real picking afternoon. */
const BURST_WINDOW_MS = 10 * 60 * 1000;

/**
 * Below this, a group costs more than it saves — the reader trades three visible rows for one row plus a
 * click. 43% of picklists have exactly one item and 73% have three or fewer, so on most of the feed no
 * grouping happens at all, which is the intent.
 */
const MIN_GROUP_SIZE = 3;

export interface ActivityGroup {
  /** Stable across renders for a React key. */
  key: string;
  rows: ActivityLogDto[];
  /** `correlation` grouped one request's rows; `burst` grouped a run of similar actions. */
  reason: 'correlation' | 'burst' | 'single';
}

/**
 * Group CONSECUTIVE rows only, preserving the server's newest-first order.
 *
 * ⚠️ **Consecutive, never a global gather.** Reordering the feed to bring related rows together would move
 * events away from the day header they belong under and destroy the timeline the panel exists to show.
 *
 * @param hasMore Whether older rows exist beyond this page. When true the FINAL run is left ungrouped: it
 *   may continue into rows the user has not fetched, and a group whose header counted only the fetched half
 *   would under-report itself — the reader would expand "3 picks" and later find there had been eleven.
 */
export function groupActivityRows(
  rows: readonly ActivityLogDto[],
  hasMore: boolean,
): ActivityGroup[] {
  const runs: ActivityLogDto[][] = [];

  for (const row of rows) {
    const current = runs[runs.length - 1];
    if (current && belongsWith(current[current.length - 1], row)) current.push(row);
    else runs.push([row]);
  }

  return runs.map((run, index) => {
    const isFinalRun = index === runs.length - 1;
    const groupable =
      run.length >= MIN_GROUP_SIZE || (run.length > 1 && sharesCorrelation(run));

    // The final run is held open when more rows exist beyond this page — see @param hasMore.
    if (!groupable || (isFinalRun && hasMore)) {
      return { key: `run-${run[0].id}`, rows: run, reason: 'single' as const };
    }

    return {
      key: `run-${run[0].id}`,
      rows: run,
      reason: sharesCorrelation(run) ? ('correlation' as const) : ('burst' as const),
    };
  });
}

/** Every row in the run came out of the same HTTP request. Null ids never match — see `belongsWith`. */
function sharesCorrelation(run: readonly ActivityLogDto[]): boolean {
  const id = run[0].correlationId;
  return id !== null && run.every((r) => r.correlationId === id);
}

function belongsWith(previous: ActivityLogDto, next: ActivityLogDto): boolean {
  // A MILESTONE never folds — before even the exact rule, deliberately (§2). A close/post/cancel/fulfil
  // is the row people scan the feed for, and hiding it behind a group header costs more than the row it
  // saves. This outranks correlation: the final pick that shares a request with PicklistFulfilled now
  // joins the preceding pick burst instead, and the fulfilment stands alone — an accepted side effect.
  // The flag is SERVER-declared on the projection entry and rides the row; the client holds no
  // ActionType set of its own (the kindMap.ts registry rule).
  if (previous.milestone || next.milestone) return false;

  // E-1 next, because it is exact. Two rows from one request belong together whatever else differs —
  // PickRecorded and PicklistFulfilled have different ActionTypes and are still one action.
  //
  // ⚠️ A null correlationId matches NOTHING, including another null. Every row written before E5 has one,
  // and treating null as a shared key would sweep an entire record's unrelated history into one group.
  if (previous.correlationId !== null && previous.correlationId === next.correlationId) return true;

  // E-2: the same person doing the same thing to the same record, without a long pause.
  return (
    previous.actorUserId !== null &&
    previous.actorUserId === next.actorUserId &&
    previous.actionType === next.actionType &&
    previous.entityType === next.entityType &&
    previous.entityId === next.entityId &&
    withinWindow(previous, next)
  );
}

function withinWindow(a: ActivityLogDto, b: ActivityLogDto): boolean {
  const gap = Math.abs(Date.parse(a.occurredAtUtc) - Date.parse(b.occurredAtUtc));
  return Number.isFinite(gap) && gap <= BURST_WINDOW_MS;
}

function formatTotal(n: number): string {
  return n % 1 === 0 ? n.toLocaleString('en-US') : n.toFixed(2);
}

/**
 * The batch sentence a collapsed BURST reads as (§3, 86d43a7cq) — `picked 15 items totaling 240 PIECE` —
 * or null when the rows cannot honestly aggregate, in which case the caller falls back to the exact
 * `N ×` form.
 *
 * ⚠️ **This sums, and that is an owner ruling (2026-08-20), not a drift.** The locked display rule —
 * "nothing is merged, summed, or synthesised" — bends for the collapsed header only: the rows inside keep
 * their own actor, time and evidence, and the row-count invariant still holds. The old header was itself
 * a bad synthesis: it borrowed ONE row's sentence and prefixed `N ×`, rendering `15 × picked 15 items` on
 * a real picklist and inviting the reader to multiply to 225.
 *
 * Sums run ONLY over the server's typed `rollup` values — never by parsing display strings — and the verb
 * comes from the rollup, so no ActionType maps to a word here.
 *
 * **Mixed unit types SUM into a single generic total** — `picked 2 items, 46 units` — by explicit owner
 * ruling (2026-08-20), made knowing 46 can be 40 KG + 6 PIECE. This deliberately overrides #492's
 * never-sum rule FOR DISPLAY ONLY; expanding the group shows the per-row truth. Do not "fix" this back
 * to a per-unit breakdown.
 */
export function aggregateBurst(rows: readonly ActivityLogDto[]): string | null {
  const verb = rows[0]?.rollup?.verb;
  if (!verb) return null;

  const codes = new Set<string>();
  const namedUnits = new Map<string, number>();
  let genericTotal = 0;

  for (const row of rows) {
    const rollup = row.rollup;
    // One row without a rollup — or a different verb — voids the batch sentence for the WHOLE group:
    // a header describing only some of its rows would under-report itself.
    if (!rollup || rollup.verb !== verb) return null;

    for (const code of rollup.itemCodes) codes.add(code);
    for (const quantity of rollup.quantities) {
      if (quantity.unit === null) genericTotal += quantity.value;
      else namedUnits.set(quantity.unit, (namedUnits.get(quantity.unit) ?? 0) + quantity.value);
    }
  }

  if (codes.size === 0) return null;

  const head = `${verb} ${codes.size} item${codes.size === 1 ? '' : 's'}`;
  const units = [...namedUnits.entries()];

  // One named unit and nothing unitless → name it. Anything mixed → the generic `units` total.
  if (units.length === 1 && genericTotal === 0) {
    const [unit, total] = units[0];
    return `${head} totaling ${formatTotal(total)} ${unit}`;
  }

  const total = units.reduce((sum, [, value]) => sum + value, genericTotal);
  return total > 0 ? `${head}, ${formatTotal(total)} units` : head;
}

/**
 * The one-line summary a collapsed group shows.
 *
 * The actor is named once because a group only forms when every row shares one.
 *
 * A correlation group NAMES its changes instead of counting them (§1): `— 2 related changes` forced an
 * expand just to learn what happened; `— posted to NetSuite as MIS-4471 · closed this picklist` does not.
 * ⚠️ Joined OLDEST → NEWEST — the rows arrive newest-first, and joining them in wire order inverts cause
 * and effect (`closed · posted` reads as closing before posting).
 *
 * A burst reads as ONE batch when its rows carry rollups (§3— see `aggregateBurst`), and falls back to
 * the exact `N ×` form when they do not.
 */
export function describeGroup(group: ActivityGroup): string {
  const [first] = group.rows;
  const n = group.rows.length;

  if (group.reason === 'correlation') {
    const story = [...group.rows].reverse().map((r) => r.summary).join(' · ');
    return `${first.actorSnapshot} — ${story}`;
  }

  const batch = aggregateBurst(group.rows);
  if (batch) return `${first.actorSnapshot} ${batch}`;

  return `${first.actorSnapshot} — ${n} × ${first.summary}`;
}

export { BURST_WINDOW_MS, MIN_GROUP_SIZE };
